import React, { useState, useRef, useEffect } from 'react';
import { X, Send, User as UserIcon, Loader2, Sparkles, BarChart3, PieChart, LineChart as LineChartIcon } from 'lucide-react';
import { askFroukjeDataAnalyst } from '../services/geminiService';
import { Vacancy } from '../types';
import { supabase } from '../services/supabaseService';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart as RePieChart, Pie, Cell,
    LineChart, Line
} from 'recharts';

interface AskFroukjeChatProps {
    isOpen: boolean;
    onClose: () => void;
    vacancies: Vacancy[];
    candidates: any[];
    aiRecruiter?: {
        name: string;
        description: string;
        avatarUrl?: string;
    };
    user?: any; // To fetch client-specific context like todos/recruiters
    onNavigateToCandidate?: (candidateId: string) => void;
}

interface Message {
    role: 'user' | 'model';
    text: string;
    chart?: {
        type: 'bar' | 'pie' | 'line';
        title: string;
        data: any[];
    } | null;
    table?: {
        headers: string[];
        rows: any[][];
    } | null;
}

const COLORS = ['#e51b23', '#e51b23', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#475569'];

const AskFroukjeChat: React.FC<AskFroukjeChatProps> = ({ isOpen, onClose, vacancies, candidates, aiRecruiter, user, onNavigateToCandidate }) => {
    const aiName = aiRecruiter?.name || 'Froukje';
    const [messages, setMessages] = useState<Message[]>([]);
    const [avatarBroken, setAvatarBroken] = useState(false);

    useEffect(() => {
        setAvatarBroken(false);
        // Init greeting with dynamic name
        setMessages([
            { role: 'model', text: `Hoi! Ik ben ${aiName}. ${aiRecruiter?.description || 'Vraag me alles over jouw talentenpool en vacatures (bijv. "Hoeveel kandidaten wonen in Amsterdam?").'}` }
        ]);
    }, [aiRecruiter, aiName]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');
        const newMessages = [...messages, { role: 'user' as const, text: userMessage }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            // Pass the previous chat history to maintain context
            const history = newMessages
                .filter(m => !m.chart && !m.table) // Skip complex objects for history
                .map(m => ({ role: m.role, content: m.text }));

            // Fetch extra context for Froukje if user is available
            let todos: any[] = [];
            let colleagues: any[] = [];

            if (user?.clientId) {
                // Postgrest builders are thenables with .then but no .catch — wrap in Promise.resolve().
                const [todosRes, profilesRes] = await Promise.all([
                    Promise.resolve(supabase.from('todos').select('*').limit(100)).catch(() => ({ data: [] })),
                    Promise.resolve(supabase.from('profiles').select('id, name, role').eq('client_id', user.clientId)).catch(() => ({ data: [] }))
                ]);
                todos = ((todosRes as { data?: unknown }).data as any[]) || [];
                colleagues = ((profilesRes as { data?: unknown }).data as any[]) || [];
            }

            const response = await askFroukjeDataAnalyst(userMessage, vacancies, candidates, history.slice(-5), aiName, todos, colleagues);

            setMessages(prev => [...prev, {
                role: 'model',
                text: response.text,
                chart: response.chart,
                table: response.table
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'model', text: 'Oeps, er ging iets mis bij het ophalen van de data.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-[450px] bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.1)] z-[100] flex flex-col border-l border-slate-100 animate-slideLeft">
            {/* Header */}
            <div className="h-16 border-b border-slate-100 flex items-center justify-between px-6 bg-gradient-to-r from-red-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#e51b23] to-pink-500 flex items-center justify-center text-white shadow-sm overflow-hidden shrink-0">
                        {aiRecruiter?.avatarUrl && !avatarBroken ? (
                            <img
                                src={aiRecruiter.avatarUrl}
                                alt="AI Avatar"
                                className="w-full h-full object-cover"
                                onError={() => setAvatarBroken(true)}
                            />
                        ) : (
                            <Sparkles size={16} />
                        )}
                    </div>
                    <div>
                        <h2 className="font-bold text-slate-900 text-sm">Vraag aan {aiName}</h2>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">HR Data Analist</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                    <X size={20} />
                </button>
            </div>

            {/* Chat History */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar bg-slate-50/50">
                {messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex max-w-[90%] gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                            {/* Avatar */}
                            <div className="flex-shrink-0 mt-1">
                                {message.role === 'model' ? (
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#e51b23] to-pink-500 flex items-center justify-center text-white shadow-sm overflow-hidden">
                                        {aiRecruiter?.avatarUrl && !avatarBroken ? (
                                            <img
                                                src={aiRecruiter.avatarUrl}
                                                alt="AI Avatar"
                                                className="w-full h-full object-cover"
                                                onError={() => setAvatarBroken(true)}
                                            />
                                        ) : (
                                            <Sparkles size={12} />
                                        )}
                                    </div>
                                ) : (
                                    <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white shadow-sm">
                                        <UserIcon size={12} />
                                    </div>
                                )}
                            </div>

                            {/* Message Content */}
                            <div className="flex flex-col gap-3">
                                {message.text && (
                                    <div className={`p-3 rounded-2xl text-sm leading-relaxed ${message.role === 'user'
                                        ? 'bg-slate-900 text-white rounded-tr-sm'
                                        : 'bg-white border border-slate-100 text-slate-700 rounded-tl-sm shadow-sm'
                                        }`}>
                                        {/* Basic markdown parsing for bold text and candidate links */}
                                        {message.text.split(/(\[[^\]]+\]\(candidate:[^)]+\))/g).map((part, i) => {
                                            const match = part.match(/\[([^\]]+)\]\(candidate:([^)]+)\)/);
                                            if (match) {
                                                return (
                                                    <button
                                                        key={i}
                                                        onClick={() => {
                                                            if (onNavigateToCandidate) onNavigateToCandidate(match[2]);
                                                        }}
                                                        className="text-[#e51b23] font-bold hover:underline transition-all inline-block border-b border-transparent hover:border-[#e51b23]"
                                                    >
                                                        {match[1]}
                                                    </button>
                                                );
                                            }
                                            return <span key={i} dangerouslySetInnerHTML={{ __html: part.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />;
                                        })}
                                    </div>
                                )}

                                {/* Render Chart if present */}
                                {message.chart && message.chart.data && (
                                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm w-full max-w-sm">
                                        <h4 className="text-xs font-bold text-slate-700 mb-4">{message.chart.title}</h4>
                                        <div className="h-[200px] w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                {message.chart.type === 'bar' ? (
                                                    <BarChart data={message.chart.data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                        <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                                        <Bar dataKey="value" fill="#e51b23" radius={[4, 4, 0, 0]}>
                                                            {message.chart.data.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                ) : message.chart.type === 'pie' ? (
                                                    <RePieChart>
                                                        <Pie data={message.chart.data} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false} style={{ fontSize: '10px' }}>
                                                            {message.chart.data.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                                    </RePieChart>
                                                ) : (
                                                    <LineChart data={message.chart.data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                        <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                                        <Line type="monotone" dataKey="value" stroke="#e51b23" strokeWidth={2} dot={{ r: 4, fill: '#e51b23', strokeWidth: 0 }} />
                                                    </LineChart>
                                                )}
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Render Table if present */}
                                {message.table && message.table.rows && (
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm w-full max-w-sm overflow-hidden">
                                        <div className="overflow-x-auto custom-scrollbar">
                                            <table className="w-full text-left text-xs">
                                                <thead className="bg-slate-50 border-b border-slate-100 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                                                    <tr>
                                                        {message.table.headers.map((h, i) => (
                                                            <th key={i} className="px-3 py-2">{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {message.table.rows.map((row, i) => (
                                                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                                            {row.map((cell, j) => (
                                                                <td key={j} className="px-3 py-2 text-slate-700">{cell}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="flex max-w-[80%] gap-3">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#e51b23] to-pink-500 flex items-center justify-center text-white shadow-sm overflow-hidden">
                                    {aiRecruiter?.avatarUrl ? (
                                        <img src={aiRecruiter.avatarUrl} alt="AI Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <Sparkles size={12} />
                                    )}
                                </div>
                            </div>
                            <div className="p-3 bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 size={14} className="animate-spin text-[#e51b23]" /> {aiName} analyseert data...
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-slate-100">
                <div className="relative flex items-center">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Stel een vraag over je data..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-4 pr-12 focus:outline-none focus:ring-2 focus:ring-[#e51b23]/20 focus:border-[#e51b23] transition-all resize-none h-[50px] min-h-[50px] max-h-[120px] text-sm overflow-hidden"
                        rows={1}
                        style={{ height: input ? 'auto' : '50px' }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        className="absolute right-2 p-2 bg-[#e51b23] text-white rounded-lg disabled:opacity-50 disabled:bg-slate-300 hover:bg-[#c61a22] transition-colors"
                    >
                        <Send size={16} />
                    </button>
                </div>
                <div className="mt-2 text-center">
                    <span className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">{aiName} beantwoordt vragen op basis van je actuele data</span>
                </div>
            </div>
        </div>
    );
};

export default AskFroukjeChat;
